// Canonical toast builder (CSS consolidation Tier 2b). Builds the one gl-toast
// card into #toast-container and lets gl_toast_controller (auto-connected via
// data-controller) run the enter / auto-dismiss / progress / leave lifecycle.
// Exposed as window.showToast (see application.js) so the ~234 existing call
// sites plus the Rails flash and turbo_stream paths keep working — the signature
// is unchanged; only the rendered look changed.

// Kind → { modifier class, Phosphor fill icon }. Accepts the legacy Bootstrap
// colorClass names (bg-success/bg-danger/bg-warning/bg-info) callers pass, plus
// the bare kind ("success" etc.).
export const TOAST_KINDS = {
  success: { mod: "gl-toast--success", icon: "ph-check-circle" },
  danger: { mod: "gl-toast--danger", icon: "ph-x-circle" },
  error: { mod: "gl-toast--danger", icon: "ph-x-circle" },
  warning: { mod: "gl-toast--warning", icon: "ph-warning" },
  info: { mod: "gl-toast--info", icon: "ph-info" },
  // A staff message to the agent ("Message from GOAT", #16746) — purple, not
  // an outcome: it is only ever the sticky server-rendered toast.
  message: { mod: "gl-toast--message", icon: "ph-envelope-simple" },
}

// Duration policy (#14564): what a toast gets when the caller passes NO
// duration. Success is glanceable, info and warnings get reading time, and an
// error stays until the user dismisses it — an error that auto-hides after 3 s
// is one the user never gets to read. A staff message stays until read too. An explicit caller duration always
// wins; 0 is indefinite. gl_toast_controller reads the same table for
// server-rendered toasts that omit data-gl-toast-duration-value.
export const TOAST_DEFAULT_DURATIONS = { success: 3000, info: 5000, warning: 8000, danger: 0, error: 0, message: 0 }
export const TOAST_FALLBACK_DURATION_MS = 3000

// Normalizes a colorClass / kind string ("bg-danger", "error") to a
// TOAST_KINDS key, falling back to success for anything unknown.
export function toastKindKey(colorClass) {
  const key = String(colorClass ?? "").replace(/^bg-/, "")
  return TOAST_KINDS[key] ? key : "success"
}

// Resolves the effective duration in ms: an explicit finite number is kept
// (anything ≤ 0 means indefinite), an omitted / non-numeric one takes the
// kind's default.
export function toastDurationFor(kind, delayMs) {
  if (delayMs === undefined || delayMs === null || delayMs === "") {
    return TOAST_DEFAULT_DURATIONS[kind] ?? TOAST_FALLBACK_DURATION_MS
  }
  const n = Number(delayMs)
  if (!Number.isFinite(n)) return TOAST_DEFAULT_DURATIONS[kind] ?? TOAST_FALLBACK_DURATION_MS
  return n > 0 ? n : 0
}

function escapeToastHtml(str) {
  const div = document.createElement("div")
  div.textContent = String(str ?? "")
  return div.innerHTML
}

// Resolves an untrusted href to a same-origin path, or null if it escapes.
//
// Deliberately resolves through the URL parser rather than prefix-matching the
// string: browsers normalize `\` to `/` for http(s) URLs, so `/\evil.com/x`
// looks path-relative but navigates cross-origin. Comparing the parsed origin
// is the only check that can't be spelled around; `javascript:` and `data:`
// fail it too, since they never parse to this origin.
function sameOriginPath(href) {
  if (typeof href !== "string" || href === "") return null

  try {
    const resolved = new URL(href, window.location.origin)
    if (resolved.origin !== window.location.origin) return null
    return resolved.pathname + resolved.search + resolved.hash
  } catch {
    return null
  }
}

// Appends an action link to a toast body. The href reaches us over ActionCable
// (see realtime_events_channel.js), so it is treated as untrusted. Built with
// DOM APIs rather than innerHTML so the label can't inject markup either.
function appendToastLink(el, href, label) {
  const path = sameOriginPath(href)
  if (!path) return

  const body = el.querySelector(".gl-toast-body")
  if (!body) return

  const link = document.createElement("a")
  link.className = "gl-toast-link"
  link.href = path
  link.textContent = String(label ?? "Download").trim() || "Download"
  body.appendChild(link)
}

// ---------------------------------------------------------------------------
// Executable actions (#14564)
//
// Two different things can sit under a toast message, and they are kept
// deliberately distinct:
//
//   • `link`   — a same-origin href (a download, a page). It may arrive from a
//                SERVER payload (ActionCable, flash) and is only ever rendered
//                as an <a>; nothing in it is executed.
//   • `action` — an executable callback supplied by CLIENT code that already
//                runs in the page (an Undo after an archive, for instance).
//                `run` MUST be a function: a string is refused, never
//                evaluated, so no remote payload can ever become behaviour.
//
// The normalized action is attached to the element as a JS property
// (`el.glToastAction`) — not a data-attribute, which could only hold a string —
// and gl_toast_controller drives it: single-flight, busy / success / failure /
// expired states, hover-and-focus hold, keyboard focus.
//
// Contract:
//   action = {
//     label:           button text (required, non-empty)
//     run:             function (required). May return a promise. Resolving to
//                      `{ ok: false, message }` is a failure; resolving to
//                      anything else (or `{ ok: true, message }`) is a success;
//                      throwing / rejecting is a failure (the error text is
//                      NEVER shown — pass a message via { ok: false } instead).
//     busyLabel:       button text while `run` is in flight     (default "Working…")
//     busyReason:      why the disabled busy button cannot be pressed — its
//                      tooltip and part of its accessible name (BUTTON-4)
//                                                               (default "Wait for it to finish")
//     successMessage:  toast title after success; when omitted the toast is
//                      dismissed instead                        (default null)
//     failureMessage:  toast title after failure                (default "That couldn't be completed.")
//     expiresInMs:     explicit DOMAIN expiry — e.g. the server's undo window.
//                      The toast drains toward it, does NOT pause on hover
//                      (the deadline is real), and at expiry the button reads
//                      `expiredLabel` and `expiredMessage` is shown.
//     expiredLabel:                                              (default "Expired")
//     expiredMessage:  detail line shown at expiry, and the disabled button's
//                      tooltip / accessible-name reason (null = no line)
//                                                               (default "The time for that has passed.")
//     focus:           move keyboard focus to the button on show (default false —
//                      a toast should not steal focus unless the caller knows
//                      the user is waiting for exactly this choice)
//   }
//
// A toast carrying an action is HELD: it never auto-dismisses regardless of
// duration, until acted on, dismissed, or its explicit domain expiry passes.
// ---------------------------------------------------------------------------
export const TOAST_ACTION_DEFAULTS = Object.freeze({
  busyLabel: "Working…",
  busyReason: "Wait for it to finish",
  successMessage: null,
  failureMessage: "That couldn't be completed.",
  expiredLabel: "Expired",
  expiredMessage: "The time for that has passed.",
  focus: false,
})

function optionalText(value, fallback) {
  if (value === null) return null
  if (value === undefined) return fallback
  const text = String(value).trim()
  return text === "" ? fallback : text
}

// Returns a normalized action or null (with a console warning) when the input
// does not meet the contract. Refusal is loud but non-fatal: the toast still
// shows, as a plain message with plain timing.
export function normalizeToastAction(action) {
  if (!action || typeof action !== "object") {
    if (action !== undefined && action !== null) console.warn("[showToast] action must be an object; ignored")
    return null
  }
  const label = String(action.label ?? "").trim()
  if (!label) {
    console.warn("[showToast] action.label is required; action ignored")
    return null
  }
  if (typeof action.run !== "function") {
    console.warn("[showToast] action.run must be a function (never a string); action ignored")
    return null
  }

  const expiresInMs = Number(action.expiresInMs)
  const expiresAt = Number.isFinite(expiresInMs) && expiresInMs > 0 ? Date.now() + expiresInMs : null

  return {
    label,
    run: action.run,
    busyLabel: optionalText(action.busyLabel, TOAST_ACTION_DEFAULTS.busyLabel) ?? TOAST_ACTION_DEFAULTS.busyLabel,
    busyReason: optionalText(action.busyReason, TOAST_ACTION_DEFAULTS.busyReason) ?? TOAST_ACTION_DEFAULTS.busyReason,
    successMessage: optionalText(action.successMessage, TOAST_ACTION_DEFAULTS.successMessage),
    failureMessage: optionalText(action.failureMessage, TOAST_ACTION_DEFAULTS.failureMessage) ?? TOAST_ACTION_DEFAULTS.failureMessage,
    expiredLabel: optionalText(action.expiredLabel, TOAST_ACTION_DEFAULTS.expiredLabel) ?? TOAST_ACTION_DEFAULTS.expiredLabel,
    expiredMessage: optionalText(action.expiredMessage, TOAST_ACTION_DEFAULTS.expiredMessage),
    expiresAt,
    focus: action.focus === true,
  }
}

// Renders the action button. Neutral outline (BUTTON-2): an Undo is a routine
// secondary choice, never the page's primary action. Built with DOM APIs so
// the label is text, not markup.
function appendToastAction(el, action) {
  const body = el.querySelector(".gl-toast-body")
  if (!body) return

  const wrap = document.createElement("div")
  wrap.className = "gl-toast-action"

  const button = document.createElement("button")
  button.type = "button"
  button.className = "gl-btn gl-btn-sm gl-btn-outline gl-toast-action-btn"
  button.textContent = action.label
  wrap.appendChild(button)
  body.appendChild(wrap)
}

// The 4th argument is either the legacy positional `{ href, label }` link or
// an options object `{ link, action }`. A link object is recognised by its
// `href`, which an options object never carries at the top level.
function normalizeToastOptions(options) {
  if (!options || typeof options !== "object") return { link: null, action: null }
  if (Object.prototype.hasOwnProperty.call(options, "href")) return { link: options, action: null }
  return {
    link: options.link && typeof options.link === "object" ? options.link : null,
    action: normalizeToastAction(options.action),
  }
}

// showToast(message, colorClass = "bg-success", delayMs = undefined, options = null)
//
// delayMs: an explicit number is honoured (0 = until the user closes it);
// omitted, the kind decides (TOAST_DEFAULT_DURATIONS). `options` is either the
// legacy `{ href, label }` link — rendered as an action link under the message,
// used by async jobs that finish with a downloadable artifact — or
// `{ link: { href, label }, action: { label, run, ... } }` (see the action
// contract above). Returns the created element (or undefined if there was no
// container / it was de-duped) — handy for tests and callers that want a handle.
export function showToast(message, colorClass = "bg-success", delayMs = undefined, options = null) {
  // Guard: not every layout renders #toast-container. The app and welcome
  // layouts both do (welcome since campaign #12564 / PE-048); a bare layout
  // without one still gets a console warning instead of a thrown error.
  const container = document.getElementById("toast-container")
  if (!container) {
    console.warn("[showToast] no #toast-container in DOM; message:", message)
    return
  }

  const key = toastKindKey(colorClass)
  const { mod, icon } = TOAST_KINDS[key]
  const text = String(message ?? "").trim()
  const { link, action } = normalizeToastOptions(options)
  const hasLink = Boolean(link && link.href)
  // An action toast is held open; its lifetime is the user's choice or the
  // action's own domain expiry, never the kind default or a caller's delay.
  const duration = action ? 0 : toastDurationFor(key, delayMs)

  // De-dupe: skip an identical message of the same kind already showing.
  //
  // Link- and action-bearing toasts opt out: each carries a *different* href
  // or callback for a different artifact / record even when the wording is
  // identical ("Your PDF of 50 leads is ready." for two 50-lead exports,
  // "Field archived" for two fields). Since the toast is the only handle the
  // user has on that download or undo, de-duping would silently discard it.
  if (!hasLink && !action) {
    for (const existing of container.querySelectorAll(".gl-toast")) {
      if (existing.glToastAction || existing.querySelector(".gl-toast-link")) continue
      const body = existing.querySelector(".gl-toast-title")
      if (body && body.textContent.trim() === text && existing.classList.contains(mod)) {
        return
      }
    }
  }

  const el = document.createElement("div")
  el.className = `gl-toast ${mod}`
  // `status` (polite) for every kind: a toast always carries a control (Dismiss,
  // and possibly an action), and an assertive region must never wrap one (A11Y-3).
  el.setAttribute("role", "status")
  el.setAttribute("aria-live", "polite")
  el.setAttribute("aria-atomic", "true")
  el.dataset.controller = "gl-toast"
  el.dataset.glToastDurationValue = String(duration)
  if (action?.expiresAt) el.dataset.glToastExpiresAtValue = String(action.expiresAt)
  const drains = duration > 0 || Boolean(action?.expiresAt)
  el.innerHTML = `
    <i class="gl-toast-icon ph-fill ${icon}" aria-hidden="true"></i>
    <div class="gl-toast-body"><div class="gl-toast-title">${escapeToastHtml(message)}</div></div>
    <button type="button" class="gl-toast-close" aria-label="Dismiss" data-gl-tooltip="Dismiss"><i class="ph ph-x"></i></button>
    ${drains ? '<span class="gl-toast-progress"></span>' : ""}
  `
  if (hasLink) appendToastLink(el, link.href, link.label)
  if (action) {
    appendToastAction(el, action)
    // Attached BEFORE the element enters the DOM so the controller's connect()
    // sees it; a property, not an attribute, so it can never be serialised or
    // supplied by markup.
    el.glToastAction = action
  }
  container.appendChild(el)
  return el
}
